/**
 * `/batches` — the gang-sheet workbench (Shopify → DTF, S3).
 *
 * A rail of sheets beside the sheet being worked on (see `BatchesPage`), and the card for one: generate
 * the sheet (async compositing, polled to READY/FAILED), preview the downscaled result, download the
 * print-ready PNG(s) for the RIP, retry a failed composite, pull list + labels, mark printed. Batches
 * are created from the Orders page and open here at `/batches/$batchId`.
 */
import {
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Brush,
  ChevronDown,
  CircleCheck,
  ClipboardCheck,
  ClipboardList,
  Download,
  Layers,
  Loader2,
  Maximize2,
  MoreHorizontal,
  QrCode,
  RefreshCw,
  Repeat2,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PAGE_CONTAINER_CLASS } from '@/components/page-container';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import {
  BATCH_STATUS_META,
  type BatchDetail,
  type BatchManifest,
  type BatchMarkPrintedOrder,
  type BatchPulledMap,
  type BatchPulledVia,
  type BatchStatus,
  type BatchSummary,
  batchLabel,
  batchShortLabel,
  blanksToPull,
  bucketOrdersByCompletion,
  compositingPollInterval,
  filterRail,
  formatInches,
  type GangSheetResult,
  isCompositing,
  LAST_OPENED_BATCH_KEY,
  type MarkPrintedConfirmKind,
  markPrintedConfirmBody,
  markPrintedConfirmKind,
  nextStep,
  type OrderCompletionGroup,
  type OrderReprintState,
  PREVIEW_MAX_UPSCALE,
  PREVIEW_NATIVE_WIDTH_PX,
  type PreviewFit,
  type PullShort,
  pickDefaultBatch,
  pressedGarmentCount,
  previewBoxHeight,
  previewFit,
  primaryAction,
  pullLeftovers,
  pullState,
  reissueLabel,
  rulerTicks,
  type ScreenPrintWorkItem,
  seqFindNumber,
  sheetSizeLabel,
  summarizeBatchItems,
  WORKING_STATUSES,
  workingSetOrder,
} from './batch-lib';
import { invalidatePullItems } from './inventory-lib';
import { LabelSheet } from './label-sheet';
import { orderCardSearch } from './order-detail-lib';
import { relativeAge } from './orders-queue';
import { PullChip } from './pull-chip';
import { PullList } from './pull-list';
import { ReprintPicker } from './reprint-picker';
import { gangSheetBatches } from './screen-print-lib';
import { useBlankNames } from './use-blank-names';

const PAGE_LIMIT = 100;

/**
 * The rail's data: the WORKING SET — one `GET /batches?status=` per working status, so it is complete
 * however long history gets (see `WORKING_STATUSES` for why the unfiltered list cannot be trusted for
 * this) — plus the paginated PRINTED history. Shared by the page's rail and the index route's default
 * card; React Query dedupes the two subscriptions onto one fetch each.
 *
 * Every key starts with 'batches', the prefix every existing invalidation already uses
 * (`qc.invalidateQueries({ queryKey: ['batches'] })`) and the change feed's `batches` domain maps to —
 * so a build, a mark-printed or a delete anywhere refreshes both sections with no new wiring. The
 * COMPOSITING query additionally polls while it has rows: the compositor is an async worker in another
 * stack and the poll is what turns a row green within seconds of the sheet landing.
 */
function useBatchRail() {
  const client = useApiClient();
  const working = useQueries({
    queries: WORKING_STATUSES.map((status) => ({
      queryKey: ['batches', 'status', status] as const,
      queryFn: async () => {
        const { data, error } = await client.GET('/batches', {
          params: { query: { limit: PAGE_LIMIT, status } },
        });
        if (error) throw new Error(error?.error?.detail ?? 'Failed to load batches.');
        if (!data?.ok) throw new Error('Empty response from the server.');
        return data.data;
      },
      // Poll only the status that changes on its own; the shape is pinned by `compositingPollInterval`'s
      // test (the payload array is `items`).
      refetchInterval:
        status === 'COMPOSITING'
          ? (q: { state: { data: unknown } }) =>
              compositingPollInterval(
                q.state.data as { items?: { status: BatchStatus }[] } | undefined,
              )
          : false,
    })),
    // `combine` re-runs on every render (it is an inline function), so the derived arrays are NEW each
    // time — nothing may hang an effect or a memo on their identity. The index route keys off the
    // picked batch ID (a string), never off these arrays.
    combine: (results) => ({
      batches: workingSetOrder(
        gangSheetBatches(results.flatMap((r) => (r.data?.items ?? []) as BatchSummary[])),
      ),
      isPending: results.some((r) => r.isPending),
      isFetching: results.some((r) => r.isFetching),
      isSuccess: results.every((r) => r.isSuccess),
      error: results.find((r) => r.isError)?.error ?? null,
    }),
  });

  // History: the existing cursor-paginated newest-first query, now PRINTED only — a manual "Load older"
  // (production-records R0), never auto-drained.
  const printed = useInfiniteQuery({
    queryKey: ['batches', 'status', 'PRINTED', 'pages'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await client.GET('/batches', {
        params: { query: { limit: PAGE_LIMIT, cursor: pageParam, status: 'PRINTED' } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load printed sheets.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  const printedData = printed.data;
  const printedBatches = useMemo(
    () => gangSheetBatches((printedData?.pages.flatMap((p) => p.items) ?? []) as BatchSummary[]),
    [printedData],
  );

  return { working, printed, printedBatches };
}

/**
 * Find by number, server-side (gang-sheets PR-5): `GET /batches?seq=` answers one batch or none. Asked
 * only when the term is a number that matches nothing LOADED — history is paginated, so a sheet from
 * months back is one request away instead of "Load older" × N. The answer is trusted only on an EXACT
 * hit: an API older than the parameter ignores it and returns the first page of everything, which must
 * not read as "found".
 */
function useSeqLookup(seq: number | null, enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['batches', 'seq', seq],
    enabled: enabled && seq != null,
    queryFn: async () => {
      const { data, error } = await client.GET('/batches', {
        params: { query: { seq: seq ?? undefined } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to look up the sheet.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return gangSheetBatches(data.data.items as BatchSummary[]).find((b) => b.seq === seq) ?? null;
    },
  });
}

/**
 * What the Printed section says when the Find term matches nothing LOADED: a number is looked up on the
 * server and its hit renders as a normal row (a link to its workbench URL); a brand keeps the hint to
 * walk history back.
 */
function FindMiss({
  seq,
  lookup,
  selectedId,
  now,
}: {
  /** The number to look up, or null when the term is a brand / the lookup is not warranted. */
  seq: number | null;
  lookup: UseQueryResult<BatchSummary | null>;
  selectedId: string | null;
  now: number;
}) {
  const hint = 'px-1 py-2 text-xs text-muted-foreground';
  if (seq == null)
    return <p className={hint}>Not in the loaded sheets — try “Load older” below.</p>;
  if (lookup.isError)
    return <p className={hint}>Couldn’t look up #{seq} — try “Load older” below.</p>;
  if (lookup.isPending) return <p className={hint}>Looking up #{seq}…</p>;
  if (!lookup.data) return <p className={hint}>No sheet #{seq}.</p>;
  return <RailRow batch={lookup.data} selected={lookup.data.batchId === selectedId} now={now} />;
}

/** The sheet last opened in this tab, if any — per viewer, never shared; absent in a private window. */
function readLastOpened(): string | null {
  try {
    return sessionStorage.getItem(LAST_OPENED_BATCH_KEY);
  } catch {
    return null;
  }
}

/** Forget it — after a delete, so the index route cannot re-open a sheet that no longer exists. */
function forgetLastOpened(): void {
  try {
    sessionStorage.removeItem(LAST_OPENED_BATCH_KEY);
  } catch {
    // Nothing to forget.
  }
}

/**
 * `/batches` — the gang-sheet WORKBENCH: a rail of sheets on the left, the sheet being worked on the
 * right, at the top of the page, always (gang-sheets redesign v1, PR-1).
 *
 * The page used to be a 100-row table with the detail card mounted UNDER it: measured on the live page,
 * the clicked sheet opened 4,762px down (88 printed rows above it), and every other sheet was the same
 * scroll again. The rail is sectioned — "To print" (the working set, fetched by status so it can never
 * fall off a page) and "Printed" (history, collapsed while there is work, open when there is none) — and
 * the work column is a child route (`/batches/$batchId`, or the index route's default card), so the URL
 * carries the selection: refresh, back and every existing `?batch=` deep link keep working (the router
 * redirects the legacy search param).
 *
 * On a phone the rail IS the page; a tap opens the sheet full-width with a back link.
 */
export function BatchesPage() {
  const isMobile = useIsMobile();
  const routeBatchId = useParams({ strict: false }).batchId ?? null;
  const rail = useBatchRail();
  // Find over the LOADED sheets, both sections — a number prefix ("13" → #130…) or a brand ("obs");
  // pair with Load older to walk history back.
  const [seqFind, setSeqFind] = useState('');
  // The Printed section: the operator's choice wins once made; until then it is open exactly when there
  // is nothing to print (today's live state) and closed when there is.
  const [printedOpen, setPrintedOpen] = useState<boolean | null>(null);

  // Remember the sheet being worked on for this tab, so the index route lands back on it (plan D2).
  useEffect(() => {
    if (!routeBatchId) return;
    try {
      sessionStorage.setItem(LAST_OPENED_BATCH_KEY, routeBatchId);
    } catch {
      // Private mode / blocked storage: the default falls back to the first Ready sheet.
    }
  }, [routeBatchId]);

  const workingBatches = rail.working.batches;
  // What the rail highlights is what the URL says — the index route navigates to its pick, so there is
  // never a card on screen whose identity the URL does not carry.
  const selectedId = routeBatchId;

  const searching = seqFind.trim() !== '';
  const workingRows = filterRail(workingBatches, seqFind);
  const printedRows = filterRail(rail.printedBatches, seqFind);
  // A number that matches nothing loaded is looked up on the server — once both sections have answered,
  // so a slow page cannot turn into a false "No sheet #N".
  const findSeq = seqFindNumber(seqFind);
  const loadedMiss =
    searching &&
    findSeq != null &&
    workingRows.length === 0 &&
    printedRows.length === 0 &&
    rail.working.isSuccess &&
    rail.printed.isSuccess;
  const lookup = useSeqLookup(findSeq, loadedMiss);
  // While searching, history opens whenever it has a match or "To print" has none (so a miss — and a
  // server hit — is visible); otherwise the operator's choice wins, and until they choose it is open
  // exactly when there is nothing to print.
  const printedEffectivelyOpen =
    (searching && (printedRows.length > 0 || workingRows.length === 0)) ||
    (printedOpen ?? (!rail.working.isPending && workingBatches.length === 0));

  const showRail = !isMobile || !routeBatchId;
  const showWork = !isMobile || routeBatchId != null;
  const now = Date.now();

  return (
    <div className={cn(PAGE_CONTAINER_CLASS, 'space-y-4')}>
      {/* No Refresh button, as on /production (#351): the change feed's `batches` domain invalidates
          every query here, and the working set polls while a sheet composites. */}
      {showRail && (
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Gang sheets</h1>
          <p className="text-sm text-muted-foreground">
            Pull the blanks, print the film, press at the scan station, then close the batch.
          </p>
        </header>
      )}

      <div
        className={cn(
          'grid items-start gap-4',
          showRail &&
            showWork &&
            'lg:grid-cols-[336px_minmax(0,1fr)] xl:grid-cols-[352px_minmax(0,1fr)]',
        )}
      >
        {showRail && (
          <aside
            aria-label="Gang sheets"
            className="min-w-0 rounded-lg border bg-muted/20 lg:sticky lg:top-[4.5rem] lg:max-h-[calc(100vh-5.5rem)] lg:overflow-y-auto"
          >
            <div className="sticky top-0 z-[1] border-b bg-muted/20 p-2 backdrop-blur">
              <Input
                value={seqFind}
                onChange={(e) => setSeqFind(e.target.value)}
                placeholder="Find #, brand…"
                className="h-9 bg-background"
                aria-label="Find a sheet by number or brand"
              />
            </div>

            <RailSection title="To print" count={workingRows.length}>
              {rail.working.isPending ? (
                <Skeleton className="h-16 w-full" />
              ) : rail.working.error ? (
                <Alert variant="destructive">
                  <AlertTitle>Couldn't load the sheets to print</AlertTitle>
                  <AlertDescription>
                    {rail.working.error instanceof Error
                      ? rail.working.error.message
                      : 'Network error.'}
                  </AlertDescription>
                </Alert>
              ) : workingRows.length === 0 ? (
                <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
                  {searching ? (
                    'No sheet to print matches.'
                  ) : (
                    <>
                      Nothing to print.
                      <br />
                      <Link
                        to="/orders"
                        className="underline underline-offset-2 hover:no-underline"
                      >
                        Build a sheet from Orders
                      </Link>
                    </>
                  )}
                </p>
              ) : (
                workingRows.map((b) => (
                  <RailRow
                    key={b.batchId}
                    batch={b}
                    selected={b.batchId === selectedId}
                    now={now}
                  />
                ))
              )}
            </RailSection>

            <Collapsible
              open={printedEffectivelyOpen}
              onOpenChange={setPrintedOpen}
              className="border-t"
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
                >
                  Printed
                  <CountChip value={printedRows.length} />
                  <ChevronDown
                    className={cn(
                      'ml-auto size-4 transition-transform',
                      !printedEffectivelyOpen && '-rotate-90',
                    )}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1.5 px-2 pb-2">
                {rail.printed.isPending && <Skeleton className="h-16 w-full" />}
                {rail.printed.isError && (
                  <Alert variant="destructive">
                    <AlertTitle>Couldn't load printed sheets</AlertTitle>
                    <AlertDescription>
                      {rail.printed.error instanceof Error
                        ? rail.printed.error.message
                        : 'Network error.'}
                    </AlertDescription>
                  </Alert>
                )}
                {printedRows.map((b) => (
                  <RailRow
                    key={b.batchId}
                    batch={b}
                    selected={b.batchId === selectedId}
                    now={now}
                  />
                ))}
                {rail.printed.isSuccess && searching && printedRows.length === 0 && (
                  <FindMiss
                    seq={loadedMiss ? findSeq : null}
                    lookup={lookup}
                    selectedId={selectedId}
                    now={now}
                  />
                )}
                {rail.printed.hasNextPage && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => rail.printed.fetchNextPage()}
                    disabled={rail.printed.isFetchingNextPage}
                  >
                    {rail.printed.isFetchingNextPage ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : null}
                    Load older sheets
                  </Button>
                )}
              </CollapsibleContent>
            </Collapsible>
          </aside>
        )}

        {showWork && (
          <div className="min-w-0 space-y-3">
            {isMobile && routeBatchId && (
              <Link
                to="/batches"
                className="inline-flex min-h-11 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="size-4" />
                Gang sheets
              </Link>
            )}
            <Outlet />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The work column when the URL names no sheet. Desktop: pick the default (`pickDefaultBatch` — last
 * opened this session → first Ready → first row) and NAVIGATE to it with `replace`, so the URL becomes
 * authoritative and Back still leaves the page (no history entry is added). Phone: nothing; the rail is
 * the page.
 *
 * ⚠️ It used to render the picked card IN PLACE (no navigation). The money review (2026-09-27) showed why
 * that is unsafe: the pick is recomputed from the rail's queries, so the operator's own Mark printed —
 * or another station's, via the change feed — refetched the working set, the default moved to the next
 * Ready sheet, and the card REMOUNTED under a new key mid-flow: the confirm (or the post-close summary)
 * vanished and a look-alike card for a different batch sat under the cursor, one click from closing the
 * wrong sheet. With the id in the URL the card's key is the route param, which a refetch never changes.
 *
 * The pick waits until every rail query has settled and nothing is in flight: right after a delete the
 * stale working list still holds the deleted id (and `lastOpened` did, until it was forgotten), and a
 * pick made in that window would land on a sheet that no longer exists.
 */
export function BatchesIndexView() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const rail = useBatchRail();
  const settled =
    rail.working.isSuccess &&
    rail.printed.isSuccess &&
    !rail.working.isFetching &&
    !rail.printed.isFetching;
  const id = settled
    ? pickDefaultBatch(rail.working.batches, rail.printedBatches, readLastOpened())
    : null;
  useEffect(() => {
    // The phone keeps the rail as the page. `useIsMobile` reads false for its first frame (it resolves
    // in an effect of its own), so the width is checked directly as well — one frame is enough to
    // navigate a phone away from its list.
    if (isMobile || (typeof window !== 'undefined' && window.innerWidth < 768)) return;
    if (id) navigate({ to: '/batches/$batchId', params: { batchId: id }, replace: true });
  }, [id, isMobile, navigate]);
  if (isMobile) return null;
  if (!settled || id) return <Skeleton className="h-64 w-full" />;
  // The rail shows the error; a card would only repeat it.
  if (rail.working.error || rail.printed.isError) return null;
  return <EmptyState />;
}

/** The work column for `/batches/$batchId`. The card fetches by id, so a deep-linked sheet renders even
 *  when it is older than the loaded history pages. */
export function BatchWorkView() {
  const { batchId } = useParams({ strict: false });
  const navigate = useNavigate();
  const qc = useQueryClient();
  if (!batchId) return null;
  return (
    <BatchDetailCard
      key={batchId}
      batchId={batchId}
      onDeleted={() => {
        // Forget it and drop its cached detail before the index re-picks, so the deleted sheet cannot
        // come back as the default — even for the one refetch window in which the stale list still
        // lists it.
        forgetLastOpened();
        qc.removeQueries({ queryKey: ['batch', batchId] });
        navigate({ to: '/batches' });
      }}
    />
  );
}

function RailSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="px-2 pb-2">
      <h2 className="flex items-center gap-2 px-1 py-2.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
        <CountChip value={count} />
      </h2>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function CountChip({ value }: { value: number }) {
  return (
    <span className="rounded-full border bg-muted px-1.5 py-px text-[11px] font-semibold normal-case tracking-normal text-muted-foreground tabular-nums">
      {value}
    </span>
  );
}

/**
 * One sheet on the rail — a LINK to its workbench URL (keyboard-reachable, `aria-current` on the open
 * one), carrying what the operator decides by: number + brand + status, then sheets · pieces · when, and
 * a third line only for the two states that need a decision or a wait.
 */
function RailRow({
  batch,
  selected,
  now,
}: {
  batch: BatchSummary;
  selected: boolean;
  now: number;
}) {
  const meta = BATCH_STATUS_META[batch.status];
  const sheets =
    batch.sheetCount != null
      ? `${batch.sheetCount} sheet${batch.sheetCount === 1 ? '' : 's'}`
      : `${batch.lineCount} line${batch.lineCount === 1 ? '' : 's'}`;
  const when =
    batch.status === 'PRINTED' && batch.printedAt
      ? `printed ${relativeAge(batch.printedAt, now)} ago`
      : `built ${relativeAge(batch.createdAt, now)} ago`;
  const reissue = reissueLabel(batch);
  const hasSp = (batch.screenPrintLocations?.length ?? 0) > 0;
  return (
    <Link
      to="/batches/$batchId"
      params={{ batchId: batch.batchId }}
      aria-current={selected ? 'page' : undefined}
      className={cn(
        'block min-w-0 rounded-md border bg-background px-3 py-2 text-sm hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected && 'border-foreground bg-muted/30 shadow-[inset_3px_0_0_0_var(--foreground)]',
        batch.status === 'FAILED' && 'border-destructive/40 bg-destructive/5',
        batch.status === 'COMPOSITING' && 'border-sky-300 dark:border-sky-900',
      )}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-semibold">{batchShortLabel(batch)}</span>
        <span className="min-w-0 truncate text-xs text-muted-foreground">
          {batch.brandLabel ?? batch.storeIds.join(', ')}
        </span>
        <Badge variant="outline" className={cn('ml-auto', meta.className)}>
          {meta.label}
        </Badge>
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
        <span>{sheets}</span>
        <span aria-hidden="true">·</span>
        <span>
          <b className="font-semibold text-foreground tabular-nums">{batch.pieceCount}</b> pcs
        </span>
        <span aria-hidden="true">·</span>
        <span>{when}</span>
        {reissue && (
          <Badge
            variant="outline"
            className="border-border bg-muted font-normal text-muted-foreground"
          >
            {reissue}
          </Badge>
        )}
        {hasSp && (
          <Badge
            variant="outline"
            className={cn(
              'font-normal',
              batch.screenPrintedAt
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'
                : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
            )}
          >
            SP {batch.screenPrintedAt ? 'done' : 'pending'}
          </Badge>
        )}
        {/* Pulled-blanks v2: the pull state in short form — quiet on an untouched sheet. */}
        <PullChip
          state={pullState(batch)}
          status={batch.status}
          now={now}
          form="short"
          hideNotStarted
          className="rounded-full px-1.5 py-px"
        />
      </div>
      {batch.status === 'READY' &&
        batch.sheetCount != null &&
        (batch.printedSheetCount ?? 0) > 0 && (
          <div className="mt-1 text-xs text-muted-foreground">
            <b className="font-semibold text-foreground tabular-nums">{batch.printedSheetCount}</b>/
            {batch.sheetCount} sheets printed
          </div>
        )}
      {batch.status === 'COMPOSITING' && (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-sky-700 dark:text-sky-300">
          <Loader2 className="size-3.5 animate-spin" />
          Nesting + compositing…
        </div>
      )}
      {batch.status === 'FAILED' && (
        <div className="mt-1 text-xs font-medium text-destructive">
          Compositing failed — retry or delete
        </div>
      )}
    </Link>
  );
}

function BatchDetailCard({ batchId, onDeleted }: { batchId: string; onDeleted: () => void }) {
  const client = useApiClient();
  const qc = useQueryClient();
  // Pulled-blanks v2: WHICH door opened the pull station rides on every row ticked from it (`via`).
  const [station, setStation] = useState<BatchPulledVia | null>(null);
  const [showLabels, setShowLabels] = useState(false);
  const [showReprint, setShowReprint] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const nameBySku = useBlankNames();

  const detailQuery = useQuery({
    queryKey: ['batch', batchId],
    // Poll while compositing so READY/FAILED lands without a manual refresh.
    refetchInterval: (q) => (isCompositing(q.state.data?.batch.status) ? 2500 : false),
    queryFn: async () => {
      const { data, error } = await client.GET('/batches/{batchId}', {
        params: { path: { batchId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the batch.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data as BatchDetail;
    },
  });

  const transition = useMutation({
    mutationFn: async (action: 'gang-sheet' | 'retry-composite') => {
      const res =
        action === 'gang-sheet'
          ? await client.POST('/batches/{batchId}/gang-sheet', { params: { path: { batchId } } })
          : await client.POST('/batches/{batchId}/retry-composite', {
              params: { path: { batchId } },
            });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Action failed.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['batch', batchId] });
      qc.invalidateQueries({ queryKey: ['batches'] });
    },
  });

  // Pulled-blanks v2: the record lives on the header (`detail.batch.pulled`); the walk's arithmetic —
  // mid-walk, a short, still out after a close — needs the LISTED rows, which come from the manifest.
  // Fetched only once a record exists, under the overlay's own key, so it is shared with the station and
  // the label sheet rather than a second read.
  const hasPullRecord = detailQuery.data?.batch.pulledAt != null;
  const manifestQuery = useQuery({
    queryKey: ['batch-manifest', batchId],
    enabled: hasPullRecord,
    queryFn: async () => {
      const { data, error } = await client.GET('/batches/{batchId}/manifest', {
        params: { path: { batchId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the pull list.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data as BatchManifest;
    },
  });

  // When the poll observes the batch settle (COMPOSITING → READY/FAILED), refresh the list
  // so its row status/sheet-count stops lagging behind the open detail card.
  const currentStatus = detailQuery.data?.batch.status;
  useEffect(() => {
    if (currentStatus === 'READY' || currentStatus === 'FAILED') {
      qc.invalidateQueries({ queryKey: ['batches'] });
    }
  }, [currentStatus, qc]);

  if (detailQuery.isPending) return <Skeleton className="h-64 w-full" />;
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Couldn't load the batch</AlertTitle>
        <AlertDescription>
          {detailQuery.error instanceof Error ? detailQuery.error.message : 'Network error.'}
        </AlertDescription>
      </Alert>
    );
  }

  const detail = detailQuery.data;
  const status = detail.batch.status;
  const meta = BATCH_STATUS_META[status];
  const summary = summarizeBatchItems(detail.items);
  const busy = transition.isPending;
  const screenPrintWork = detail.batch.screenPrintWork ?? [];
  /**
   * ⚠️ Closed on a sheet that is ITSELF a reprint — a deliberate regression, stated rather than
   * inherited. A reprint re-numbers its copies from 0, and the copyIndex → source-rank mapping
   * (`reissueRanks`) lives on the batch ITEM and is not on the manifest the picker reads. So ticking
   * "garment 1" of a reprint sheet would queue rank 1 of the ORIGINAL line, which may be a different
   * shirt entirely: the wrong garment is reprinted, the frozen `batchedQty` is wrong (it is the flush's
   * owed denominator), and the retire's rank lookup never matches. Report it from the press station or
   * the order instead. Re-open this once `reissueRanks` reaches the manifest.
   */
  const reprintBlocked = detail.batch.reissueOf
    ? {
        title: "Report a reprint's defects from the press station",
        detail:
          "This sheet is itself a reprint, and its copy numbers are its own — they don't name the garments on the original order. Scanning the garment at the press station reports it against the right shirt.",
      }
    : null;
  // A mixed batch can't be marked printed until its screen-print work is done (money-waster #1).
  const screenPrintPending = screenPrintWork.length > 0 && detail.batch.screenPrintedAt == null;
  const printReadyUrls = detail.gangSheet?.printReadyUrls ?? [];
  const canDelete = status !== 'COMPOSITING' && status !== 'PRINTED';
  const spLocations = detail.batch.screenPrintLocations ?? [];
  const spDone = detail.batch.screenPrintedAt != null;
  // Pulled-blanks v2: the sheet's pull state, with the walk's arithmetic once the manifest is in.
  const pullRows =
    hasPullRecord && manifestQuery.data
      ? blanksToPull(manifestQuery.data, nameBySku, screenPrintWork)
      : undefined;
  const pull = pullState(detail.batch, pullRows);
  const now = Date.now();
  // One rule feeds both the red button and the "Next:" line (plan D6).
  const stepInput = {
    status,
    sheetCount: detail.gangSheet?.sheetCount ?? 0,
    printedSheetCount: detail.gangSheet?.sheets.filter((sh) => sh.printedAt).length ?? 0,
    screenPrintPending,
    // `undefined` while the rows are still loading — never a sentence built on a guess.
    pullDone: status === 'READY' ? (pull.kind === 'pulled' ? pull.complete : false) : undefined,
  };
  const primary = primaryAction(stepInput);
  const next = nextStep(stepInput);
  const pullIncomplete = pull.kind !== 'pulled' || pull.complete === false;
  const stillOut = status === 'PRINTED' && pull.kind === 'pulled' ? pull.stillOut : [];

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <CardTitle className="flex items-center gap-2 text-base">
              {batchLabel(detail.batch)}
              <Badge variant="outline" className={meta.className}>
                {meta.label}
              </Badge>
              {reissueLabel(detail.batch) && (
                <Badge variant="outline" className="border-border bg-muted text-muted-foreground">
                  {reissueLabel(detail.batch)}
                </Badge>
              )}
              {detail.batch.fromReprintQueue && (
                <Badge variant="outline" className="border-border bg-muted text-muted-foreground">
                  From the reprint queue
                </Badge>
              )}
            </CardTitle>
            {/* Counts as chips + a single SP status pill — was a run of muted text. */}
            <div className="flex flex-wrap items-center gap-1.5">
              <StatChip
                value={summary.lineCount}
                label={summary.lineCount === 1 ? 'line' : 'lines'}
              />
              <StatChip value={summary.pieceCount} label="pieces" />
              <StatChip
                value={summary.designCount}
                label={summary.designCount === 1 ? 'design' : 'designs'}
              />
              {spLocations.length > 0 && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs',
                    spDone
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'
                      : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
                  )}
                >
                  Screen print · {spLocations.join(', ')} · {spDone ? 'Done' : 'Pending'}
                </span>
              )}
              {status === 'PRINTED' && detail.batch.printedAt && (
                <span className="inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300">
                  Printed {relativeAge(detail.batch.printedAt, Date.now())}
                </span>
              )}
              {(status === 'READY' || status === 'PRINTED') && (
                <PullChip state={pull} status={status} now={now} />
              )}
            </div>
          </div>

          {/* Delete lives in ⋯ beside the title — destructive, away from the routine actions — and keeps
              its lockout while COMPOSITING / PRINTED. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="px-2" aria-label="More actions">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                disabled={!canDelete}
                onSelect={() => setDeleteOpen(true)}
              >
                <Trash2 className="size-4" />
                Delete sheet
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* The action bar, grouped by the job's steps — Print · Cut & press · Close — with exactly ONE
            red primary per state (`primaryAction`), and the "Next:" line derived from the same input so
            the sentence and the red button cannot disagree. Every control below existed before; what
            changed is where it sits and which one is red. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-3">
          {status === 'COMPOSITING' && (
            <span className="text-xs text-muted-foreground">Nothing to do until it lands.</span>
          )}
          {(status === 'DRAFT' || status === 'FAILED') && (
            <ActionGroup label={status === 'DRAFT' ? 'Print' : 'Fix'}>
              <BatchActions
                status={status}
                busy={busy}
                onGenerate={() => transition.mutate('gang-sheet')}
                onRetry={() => transition.mutate('retry-composite')}
              />
            </ActionGroup>
          )}
          {(status === 'READY' || status === 'PRINTED') && (
            <>
              {printReadyUrls.length > 0 && (
                <ActionGroup label="Print">
                  {printReadyUrls.length > 1 ? (
                    <Button
                      size="sm"
                      variant={primary === 'download' ? undefined : 'outline'}
                      onClick={() => downloadSheets(printReadyUrls)}
                    >
                      <Download className="size-4" />
                      Download all ({printReadyUrls.length})
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant={primary === 'download' ? undefined : 'outline'}
                      asChild
                    >
                      {/* No target=_blank: the URL's content-disposition downloads the file by name. */}
                      <a href={printReadyUrls[0]} download rel="noreferrer">
                        <Download className="size-4" />
                        Download sheet
                      </a>
                    </Button>
                  )}
                </ActionGroup>
              )}
              <ActionGroup label="Cut & press">
                <Button variant="outline" size="sm" onClick={() => setStation('pull-list')}>
                  <ClipboardList className="size-4" />
                  Pull list
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowLabels(true)}>
                  <QrCode className="size-4" />
                  Print labels
                </Button>
                {/* Pulled-blanks v2 (D2): the card opens the ROWS — it never writes a whole sheet in one
                    tap. "Tick the pull list" while the walk is unconfirmed or under way; "Edit rows"
                    once every row is ticked; "Returned to the shelf" for a closed sheet's leftover. */}
                {status === 'READY' && pullIncomplete && (
                  <Button variant="outline" size="sm" onClick={() => setStation('card')}>
                    <ClipboardCheck className="size-4" />
                    Tick the pull list
                  </Button>
                )}
                {pull.kind === 'pulled' && !pullIncomplete && (
                  <Button variant="outline" size="sm" onClick={() => setStation('card')}>
                    <ClipboardCheck className="size-4" />
                    Edit rows
                  </Button>
                )}
                {stillOut.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setReturnOpen(true)}>
                    <Undo2 className="size-4" />
                    Returned to the shelf
                  </Button>
                )}
                {/* Disabled, not hidden, on a sheet that is itself a reprint: the reason rides on the
                    button and inside the picker, so the bench learns where to report it instead. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowReprint(true)}
                  disabled={reprintBlocked !== null}
                  title={reprintBlocked?.detail}
                >
                  <Repeat2 className="size-4" />
                  Reprint a transfer
                </Button>
              </ActionGroup>
              <span className="flex-1" aria-hidden="true" />
              <ActionGroup label="Close">
                <MarkPrintedControl
                  batchId={batchId}
                  status={status}
                  label={batchLabel(detail.batch)}
                  screenPrintLocations={detail.batch.screenPrintLocations}
                  screenPrintPending={screenPrintPending}
                  screenPrintWork={screenPrintWork}
                  pulled={detail.batch.pulled}
                  pulledRev={detail.batch.pulledRev}
                  emphasis={primary === 'markPrinted' ? 'primary' : 'outline'}
                  onChanged={() => qc.invalidateQueries({ queryKey: ['batch', batchId] })}
                />
              </ActionGroup>
            </>
          )}
        </div>
        {next && <p className="text-xs text-muted-foreground">{next}</p>}
        {pull.kind === 'pulled' && pull.rows != null && (
          <p className="text-xs text-muted-foreground" data-testid="pull-detail">
            {pull.ticked} of {pull.rows} row{pull.rows === 1 ? '' : 's'} ticked · {pull.units} blank
            {pull.units === 1 ? '' : 's'} off the shelf, still in the book until pressed
            {pull.shorts.length > 0
              ? ` · short: ${pull.shorts.map((x) => `${x.label} ${x.variant.replace(/ · /g, ' ')} ${x.got} of ${x.listed}`).join(', ')}`
              : ''}
          </p>
        )}
      </CardHeader>
      <DeleteBatchDialog
        batchId={batchId}
        lineCount={summary.lineCount}
        pulledUnits={detail.batch.pulledUnits ?? 0}
        pressedGarments={pressedGarmentCount(detail.items, screenPrintWork)}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={onDeleted}
      />
      {returnOpen && (
        <ReturnedDialog
          batchId={batchId}
          label={batchLabel(detail.batch)}
          stillOut={stillOut}
          rows={pullLeftovers(detail.batch.pulled, pullRows ?? []).returnedRows}
          rev={detail.batch.pulledRev ?? null}
          onClose={() => setReturnOpen(false)}
        />
      )}
      {showLabels && (
        <LabelSheet
          batchId={batchId}
          screenPrintWork={screenPrintWork}
          batchTitle={batchLabel(detail.batch)}
          onClose={() => setShowLabels(false)}
        />
      )}
      <CardContent className="space-y-5">
        {transition.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {transition.error instanceof Error ? transition.error.message : 'Action failed.'}
            </AlertDescription>
          </Alert>
        )}

        {status === 'COMPOSITING' && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Nesting + compositing the sheet… this refreshes automatically.
          </div>
        )}

        {status === 'FAILED' && detail.batch.failureReason && (
          <Alert variant="destructive">
            <AlertTitle>Compositing failed</AlertTitle>
            <AlertDescription>{detail.batch.failureReason}</AlertDescription>
          </Alert>
        )}

        {(status === 'READY' || status === 'PRINTED') && detail.gangSheet && (
          <FilmAndSheets gangSheet={detail.gangSheet} batchId={batchId} />
        )}

        {(status === 'READY' || status === 'PRINTED') && station && (
          <PullList
            batchId={batchId}
            label={batchLabel(detail.batch)}
            screenPrintWork={screenPrintWork}
            via={station}
            onClose={() => setStation(null)}
          />
        )}

        {(status === 'READY' || status === 'PRINTED') && showReprint && (
          <ReprintPicker
            batchId={batchId}
            label={batchLabel(detail.batch)}
            blocked={reprintBlocked}
            onClose={() => setShowReprint(false)}
          />
        )}

        {screenPrintWork.length > 0 && (
          <ScreenPrintWorklist
            items={screenPrintWork}
            done={detail.batch.screenPrintedAt != null}
            batchId={batchId}
            status={status}
            onChanged={() => qc.invalidateQueries({ queryKey: ['batch', batchId] })}
          />
        )}

        <ItemsSection items={detail.items} />
      </CardContent>
    </Card>
  );
}

/**
 * Delete (unbuild) a batch: returns its lines to the Orders queue and removes the sheet. A
 * COMPOSITING batch can't be deleted (the server rejects it), so the button is disabled then.
 */
/**
 * Delete confirmation, controlled by the parent so it can be opened from the header's ⋯ overflow menu
 * (destructive actions live there, not inline with routine ones). The COMPOSITING/PRINTED guard now
 * lives on the menu item that opens this.
 */
export function DeleteBatchDialog({
  batchId,
  lineCount,
  pulledUnits = 0,
  pressedGarments = 0,
  open,
  onOpenChange,
  onDeleted,
}: {
  batchId: string;
  lineCount: number;
  /** Pulled-blanks v2 §2.7: blanks the pull station recorded for this sheet — the record dies with it. */
  pulledUnits?: number;
  /** Garments whose blank the press already drew — the unbuild hands those blanks back to the book. */
  pressedGarments?: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onDeleted: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();

  const remove = useMutation({
    mutationFn: async () => {
      const res = await client.DELETE('/batches/{batchId}', { params: { path: { batchId } } });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Delete failed.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: () => {
      // Sheet gone from the list; its lines are back in the Orders queue.
      qc.invalidateQueries({ queryKey: ['batches'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      // Reopened lines change which orders are producible → the production board's lanes move.
      qc.invalidateQueries({ queryKey: ['production-board'] });
      onOpenChange(false);
      onDeleted();
    },
  });

  // Clear any prior error each time the dialog opens, so a reopened dialog starts clean.
  const { reset } = remove;
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  return (
    <Dialog open={open} onOpenChange={(v) => !remove.isPending && onOpenChange(v)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this gang sheet?</DialogTitle>
          <DialogDescription>
            Its {lineCount} line{lineCount === 1 ? '' : 's'} return to the Orders queue so you can
            rebuild. The composited sheet is discarded — this can't be undone.
          </DialogDescription>
        </DialogHeader>
        {pressedGarments > 0 && (
          <Alert variant="destructive">
            <AlertDescription>
              {pressedGarments} garment{pressedGarments === 1 ? ' is' : 's are'} already pressed on
              this sheet — close it with Mark printed instead; deleting hands {pressedGarments}{' '}
              blank
              {pressedGarments === 1 ? '' : 's'} back to the book for shirts that exist.
            </AlertDescription>
          </Alert>
        )}
        {pulledUnits > 0 && (
          <Alert>
            <AlertDescription>
              {pulledUnits} blank{pulledUnits === 1 ? ' is' : 's are'} recorded as pulled for this
              sheet. Keep the stacks sorted and tap Pulled again on the rebuilt sheet, or peel the
              labels and re-shelve them. Until the rebuilt sheet is confirmed, a shelf count would
              treat
              {pulledUnits === 1 ? ' that blank' : ` those ${pulledUnits}`} as shelf.
            </AlertDescription>
          </Alert>
        )}
        {remove.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {remove.error instanceof Error ? remove.error.message : 'Delete failed.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => remove.mutate()} disabled={remove.isPending}>
            {remove.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
            Delete sheet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Close the production loop: mark a READY batch PRINTED (its lines flip BATCHED→PRINTED) or undo a
 * PRINTED one back to READY. Marking opens a confirm that reminds the operator of any screen-print
 * work produced OFF the sheet (press it before marking, or a garment ships half-done — money-waster
 * #1), then shows which orders the print just completed vs which are still waiting on another sheet.
 */
export function MarkPrintedControl({
  batchId,
  status,
  screenPrintLocations,
  screenPrintPending,
  screenPrintWork = [],
  pulled,
  pulledRev,
  emphasis = 'primary',
  label,
  onChanged,
}: {
  batchId: string;
  status: BatchStatus;
  /** Pulled-blanks v2 §2.5 door 6: the sheet's pull record and its revision, for the leftover question
   *  (a recorded blank the sheet no longer owes — refunded after the pull — back on the shelf, or still
   *  out?). Absent on a sheet nobody ticked. */
  pulled?: BatchPulledMap;
  pulledRev?: number;
  /** The batch's screen-print placements — a filmless run's rows come from here. */
  screenPrintWork?: readonly ScreenPrintWorkItem[];
  /** Names the batch in the confirm ("Mark Batch #141 printed?") so the dialog carries its own
   *  identity — the card around it can change under the operator (another station closing a sheet
   *  moves the rail); the sentence must not. Absent → the old "this sheet" wording. */
  label?: string;
  screenPrintLocations?: string[];
  /** True for a mixed batch whose screen-print work isn't done yet — Mark printed is gated. */
  screenPrintPending: boolean;
  /** Red only when it is THE next step (every sheet ticked) — see `primaryAction`; outline otherwise.
   *  The gated (dashed) and Undo variants are unaffected. Default keeps every other caller as it was. */
  emphasis?: 'primary' | 'outline';
  onChanged: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<BatchMarkPrintedOrder[] | null>(null);
  // R1.1: the server refused the close because garments on the sheet have no press record —
  // holds the server's own message, rendered as an explicit second confirm (never as an error).
  /**
   * A pending server-demanded confirmation, and WHICH one. The two are deliberately distinct consents
   * server-side (a reissue flips no lines, so `confirmBulkSettle` never applies to it), so the retry
   * must echo back the same kind the server asked for — sending the wrong flag re-trips the gate.
   */
  const [bulkConfirm, setBulkConfirm] = useState<{
    kind: 'bulkSettle' | 'reprintClose';
    message: string;
  } | null>(null);
  // Pulled-blanks v2 — the leftover question. The listed rows (what the sheet still owes) come from the
  // manifest, read only while the confirm is open on a sheet with a record; `returned` holds the skus the
  // operator marked back on the shelf (default: unanswered = still out, nothing written).
  const nameBySku = useBlankNames();
  const hasPullRecord = pulled != null && Object.keys(pulled).length > 0;
  const [returned, setReturned] = useState<ReadonlySet<string>>(new Set());
  /** The Returned write has landed for this confirm — the question is answered whatever the close does next
   *  (a server-demanded second confirm keeps the dialog open with the props still at the OLD revision). */
  const [returnedDone, setReturnedDone] = useState(false);
  const manifestQuery = useQuery({
    queryKey: ['batch-manifest', batchId],
    enabled: open && status === 'READY' && hasPullRecord,
    queryFn: async () => {
      const { data, error } = await client.GET('/batches/{batchId}/manifest', {
        params: { path: { batchId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the pull list.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data as BatchManifest;
    },
  });
  const leftover =
    hasPullRecord && manifestQuery.data
      ? pullLeftovers(pulled, blanksToPull(manifestQuery.data, nameBySku, screenPrintWork))
      : null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['batch', batchId] });
    qc.invalidateQueries({ queryKey: ['batches'] });
    qc.invalidateQueries({ queryKey: ['batch-manifest', batchId] });
    qc.invalidateQueries({ queryKey: ['orders'] });
    // Mark/unmark-printed flips every line's workflowState, which is exactly what the production
    // board derives its ship / don't-ship lanes from — and ['production-board'] had NO invalidator
    // anywhere in the app, so the board only ever refreshed on a remount.
    qc.invalidateQueries({ queryKey: ['production-board'] });
    onChanged();
  };

  const mark = useMutation({
    mutationFn: async (vars: { confirm?: MarkPrintedConfirmKind }) => {
      // The leftover answer goes FIRST, as its own write (§2.6: *Returned* reduces the row to what the
      // sheet owes — a `pulled` edit via `close`). It fails loud and the close does not proceed; once it
      // has landed the answers are cleared, so a retry of the close never re-sends it.
      if (returned.size > 0 && leftover && pulled) {
        const rows = Object.entries(pulled).flatMap(([ssSku, r]) => {
          if (!returned.has(ssSku)) return [{ ssSku, qty: r.qty }];
          const back = leftover.returnedRows.find((x) => x.ssSku === ssSku);
          return back ? [back] : [];
        });
        const r = await client.POST('/batches/{batchId}/pulled', {
          params: { path: { batchId } },
          body: { rev: pulledRev ?? null, rows, via: 'close' },
        });
        if (r.error) {
          throw new Error(r.error?.error?.detail ?? 'Could not record the return to the shelf.');
        }
        setReturned(new Set());
        setReturnedDone(true);
        // The header's record and revision moved; the card must not keep offering the old ones.
        qc.invalidateQueries({ queryKey: ['batch', batchId] });
        qc.invalidateQueries({ queryKey: ['batch-manifest', batchId] });
        invalidatePullItems(qc);
      }
      const res = await client.POST('/batches/{batchId}/mark-printed', {
        params: { path: { batchId } },
        body: vars.confirm ? markPrintedConfirmBody(vars.confirm) : {},
      });
      if (res.error) {
        // The server wants an explicit manager confirm. Surface its message as the confirm prompt,
        // not as a failure. (A non-admin gets a plain 403 whose detail renders through the normal
        // error path below.) The two kinds are SEPARATE consents — see `markPrintedConfirmKind`.
        const kind = markPrintedConfirmKind(res.error?.error?.type);
        if (kind) {
          setBulkConfirm({
            kind,
            message:
              res.error.error.detail ??
              (kind === 'reprintClose'
                ? 'Transfers on this reprint sheet have no press record — confirm to close anyway.'
                : 'Garments on this sheet have no press record — confirm to close anyway.'),
          });
          return null;
        }
        throw new Error(res.error?.error?.detail ?? 'Failed to mark printed.');
      }
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: (data) => {
      if (!data) return; // the bulk-settle confirm prompt path — the dialog stays open
      setResult(data.orders);
      invalidate();
    },
  });

  const unmark = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/batches/{batchId}/unmark-printed', {
        params: { path: { batchId } },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to undo.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: invalidate,
  });

  const hasScreenPrint = screenPrintLocations != null && screenPrintLocations.length > 0;
  // The CONFIRM can only be open while the batch is still READY and not gated on screen print.
  const confirmable = status === 'READY' && !screenPrintPending;
  // The post-close SUMMARY outlives the status flip its own success causes: `invalidate()` refetches
  // the batch as PRINTED, and this control used to answer that by returning the Undo button alone —
  // which unmounted the dialog, so the operator never saw which orders the print completed (gang-sheets
  // money review, F2). The dialog is therefore ONE element that every branch below renders, open while
  // there is a result to show whatever the status says.
  const showingResult = result != null;
  const dialogOpen = showingResult || (open && confirmable);

  const close = () => {
    setOpen(false);
    mark.reset();
    setResult(null);
    setBulkConfirm(null);
    setReturned(new Set());
    setReturnedDone(false);
  };

  // When the batch stops being confirmable UNDER an open confirm (another station closed it), the
  // dialog is forced shut above — drop the stale `open` too, or the confirm would pop back up by itself
  // the next time the batch is READY (an Undo, a retry).
  useEffect(() => {
    if (open && !showingResult && !confirmable) {
      setOpen(false);
      setBulkConfirm(null);
    }
  }, [open, showingResult, confirmable]);

  const dialog = (
    <Dialog
      key="mark-printed-dialog"
      open={dialogOpen}
      onOpenChange={(v) => {
        if (mark.isPending) return;
        if (!v) close();
      }}
    >
      <DialogContent>
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>Marked printed</DialogTitle>
              <DialogDescription>Where the orders on this sheet stand now.</DialogDescription>
            </DialogHeader>
            <OrderCompletionSummary orders={result} />
            <DialogFooter>
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Mark {label ?? 'this sheet'} printed?</DialogTitle>
              <DialogDescription>
                Its lines move to Printed and the sheet leaves the “to print” list. You can undo
                this.
              </DialogDescription>
            </DialogHeader>
            {hasScreenPrint && (
              <Alert>
                <AlertTitle>Screen-print work on this batch</AlertTitle>
                <AlertDescription>
                  This batch also has screen print produced off the sheet (
                  {screenPrintLocations?.join(', ')}). Press those before marking printed —
                  otherwise a garment could ship half-done.
                </AlertDescription>
              </Alert>
            )}
            {returnedDone ? (
              <p className="text-sm text-muted-foreground" data-testid="leftover-done">
                Returned to the shelf — recorded.
              </p>
            ) : (
              leftover &&
              leftover.leftovers.length > 0 && (
                <LeftoverQuestion
                  leftovers={leftover.leftovers}
                  returned={returned}
                  onChange={setReturned}
                />
              )
            )}
            {bulkConfirm && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                <AlertTitle>Unrecorded garments on this sheet</AlertTitle>
                <AlertDescription>{bulkConfirm.message}</AlertDescription>
              </Alert>
            )}
            {mark.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {mark.error instanceof Error ? mark.error.message : 'Failed to mark printed.'}
                </AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={mark.isPending}>
                Cancel
              </Button>
              {bulkConfirm ? (
                <Button
                  variant="destructive"
                  onClick={() => mark.mutate({ confirm: bulkConfirm.kind })}
                  disabled={mark.isPending}
                >
                  {mark.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <CircleCheck className="size-4" />
                  )}
                  Close anyway
                </Button>
              ) : (
                <Button onClick={() => mark.mutate({})} disabled={mark.isPending}>
                  {mark.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <CircleCheck className="size-4" />
                  )}
                  Mark printed
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  if (status === 'PRINTED') {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => unmark.mutate()}
          disabled={unmark.isPending}
        >
          {unmark.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Undo2 className="size-4" />
          )}
          Undo printed
        </Button>
        {dialog}
      </>
    );
  }
  if (status !== 'READY') return showingResult ? dialog : null;

  // Gated: screen print must be marked done first (money-waster #1). Disable with an explanatory
  // title rather than let the click 409.
  if (screenPrintPending) {
    // Gated on screen print, not broken: a dashed outline with an inline reason reads as "blocked,
    // do this first" — unlike a disabled primary, which looked like a washed-out error state.
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          disabled
          className="border-dashed"
          title="Press the screen print first (at the press station), then mark the batch printed."
        >
          <CircleCheck className="size-4" />
          Mark printed
          <span className="text-amber-600 dark:text-amber-400">· screen print first</span>
        </Button>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant={emphasis === 'outline' ? 'outline' : undefined}
        onClick={() => {
          mark.reset();
          setResult(null);
          setBulkConfirm(null);
          setOpen(true);
        }}
      >
        <CircleCheck className="size-4" />
        Mark printed
      </Button>
      {dialog}
    </>
  );
}

/**
 * Pulled-blanks v2 §2.5 door 6 — the close's leftover question. One line per recorded blank the sheet no
 * longer owes (refunded after the pull): *Returned to the shelf* reduces that row to the owed quantity
 * when the close is confirmed; *Still out — leave it* (the default) writes nothing and the closed sheet's
 * card keeps saying so. Selection is carried by `variant="secondary"` + `aria-pressed`, never by the
 * primary fill (this theme's primary and destructive are the same red).
 */
function LeftoverQuestion({
  leftovers,
  returned,
  onChange,
}: {
  leftovers: readonly PullShort[];
  returned: ReadonlySet<string>;
  onChange: (next: ReadonlySet<string>) => void;
}) {
  const set = (ssSku: string, back: boolean) => {
    const next = new Set(returned);
    if (back) next.add(ssSku);
    else next.delete(ssSku);
    onChange(next);
  };
  return (
    <div className="space-y-3 rounded-md border p-3 text-sm" data-testid="leftover-question">
      {leftovers.map((l) => {
        const n = l.got - l.listed;
        const back = returned.has(l.ssSku);
        return (
          <div key={l.ssSku} className="space-y-1.5">
            <p>
              <b>
                {n} {l.label}
                {l.variant ? ` · ${l.variant}` : ''}
              </b>{' '}
              {n === 1 ? 'was' : 'were'} pulled for this sheet and not pressed (refunded).{' '}
              {n === 1 ? 'Is it' : 'Are they'} back on the shelf?
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={back ? 'secondary' : 'outline'}
                aria-pressed={back}
                onClick={() => set(l.ssSku, true)}
              >
                {back && <CircleCheck className="size-3.5" />}
                Returned to the shelf
              </Button>
              <Button
                size="sm"
                variant={back ? 'outline' : 'secondary'}
                aria-pressed={!back}
                onClick={() => set(l.ssSku, false)}
              >
                {!back && <CircleCheck className="size-3.5" />}
                Still out — leave it
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The closed sheet's *Returned to the shelf* (§2.6 — D7's card action): the same `pulled` edit the close
 * offers, for a leftover nobody answered then. Posts the record with each still-out row reduced to what
 * the sheet drew; the book and the shelf meet without a count.
 */
function ReturnedDialog({
  batchId,
  label,
  stillOut,
  rows,
  rev,
  onClose,
}: {
  batchId: string;
  label: string;
  stillOut: readonly PullShort[];
  /** The complete record after the return — every still-out row at its owed quantity. */
  rows: { ssSku: string; qty: number }[];
  rev: number | null;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const write = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/batches/{batchId}/pulled', {
        params: { path: { batchId } },
        body: { rev, rows, via: 'card' },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Could not record the return.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['batch', batchId] });
      qc.invalidateQueries({ queryKey: ['batches'] });
      qc.invalidateQueries({ queryKey: ['batch-manifest', batchId] });
      // A returned leftover clears the hub's "still out" alert on its next visit.
      invalidatePullItems(qc);
      onClose();
    },
  });
  const total = stillOut.reduce((s, x) => s + (x.got - x.listed), 0);
  return (
    <Dialog open onOpenChange={(v) => !v && !write.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Back on the shelf?</DialogTitle>
          <DialogDescription>
            {total} blank{total === 1 ? '' : 's'} pulled for {label} {total === 1 ? 'was' : 'were'}{' '}
            not pressed. Returned sets the record to what the press drew — the book and the shelf
            meet without a count.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1 text-sm">
          {stillOut.map((l) => (
            <li key={l.ssSku}>
              <b>{l.got - l.listed}</b> {l.label}
              {l.variant ? ` · ${l.variant}` : ''}
            </li>
          ))}
        </ul>
        {write.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {write.error instanceof Error ? write.error.message : 'Could not record the return.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={write.isPending}>
            Still out — leave it
          </Button>
          <Button onClick={() => write.mutate()} disabled={write.isPending}>
            {write.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Undo2 className="size-4" />
            )}
            Returned to the shelf
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Groups the mark-printed rollup into "completed by this print" vs "still waiting" vs "needs attention".
 *
 * ⚠️ The green "Completed" line is a ship claim, shown at the moment the floor collates and bags this
 * sheet's orders — so it is gated on the outstanding-reprint lookup exactly like the order card and the
 * board (decision D4 / §8.25). The rollup status alone cannot see a reprint: a reissue never repoints the
 * source line's `batchId`, so a defected-and-reprinting order rolls up PRINTED regardless.
 *
 * Gated CLIENT-SIDE against the endpoint that already exists rather than by widening
 * `BatchMarkPrintedResult`: it keeps a bounded scan off the mark-printed money write, adds no latency to
 * it, and reuses a route that is already authorized and tested. One request per touched order, after the
 * write, inside a modal — a handful at most.
 */
function OrderCompletionSummary({ orders }: { orders: readonly BatchMarkPrintedOrder[] }) {
  const client = useApiClient();
  const reprintQueries = useQueries({
    queries: orders.map((o) => ({
      queryKey: ['order-reprints', o.storeId, o.orderId],
      queryFn: async () => {
        const { data, error } = await client.GET('/orders/{storeId}/{orderId}/reprints', {
          params: { path: { storeId: o.storeId, orderId: o.orderId } },
        });
        if (error) throw new Error(error?.error?.detail ?? 'Failed to load reprints.');
        if (!data?.ok) throw new Error('Empty response from the server.');
        return data.data;
      },
    })),
  });

  // `isSuccess`, not "data is present" — a failed lookup must not inherit a stale all-clear. `.map` over
  // the query array keeps state[i] aligned with orders[i] BY CONSTRUCTION, which hand-indexing did not:
  // the zip and its fail-safe for a length mismatch live in `bucketOrdersByCompletion`, under test.
  const reprintStates: OrderReprintState[] = reprintQueries.map((q) => ({
    checked: q.isSuccess,
    outstanding: q.data?.reprints.length ?? 0,
  }));
  const bucketed = bucketOrdersByCompletion(orders, reprintStates);
  const inBucket = (group: OrderCompletionGroup) =>
    bucketed.filter((b) => b.bucket === group).map((b) => b.order);

  const completed = inBucket('completed');
  const reprinting = inBucket('reprint');
  const unverified = inBucket('unverified');
  const pending = inBucket('pending');
  const attention = inBucket('attention');

  const line = (o: BatchMarkPrintedOrder) => {
    const p = o.production;
    const done = p.printed + p.shipped + p.fulfilled;
    const producible = p.open + p.batched + done;
    return `${o.orderName} (${done}/${producible} done)`;
  };

  return (
    <div className="space-y-2 text-sm">
      <p className="text-emerald-700 dark:text-emerald-300">
        <span className="font-medium">Completed:</span>{' '}
        {completed.length > 0 ? completed.map((o) => o.orderName).join(', ') : 'none'}
      </p>
      {reprinting.length > 0 && (
        <p className="text-destructive">
          <span className="font-medium">Reprint on the press — don’t ship:</span>{' '}
          {reprinting.map((o) => o.orderName).join(', ')}
        </p>
      )}
      {unverified.length > 0 && (
        <p className="text-amber-700 dark:text-amber-300">
          <span className="font-medium">Couldn’t check for reprints — verify before shipping:</span>{' '}
          {unverified.map((o) => o.orderName).join(', ')}
        </p>
      )}
      {pending.length > 0 && (
        <p className="text-muted-foreground">
          <span className="font-medium">Still in production:</span> {pending.map(line).join(', ')}
        </p>
      )}
      {attention.length > 0 && (
        <p className="text-amber-700 dark:text-amber-300">
          <span className="font-medium">Needs attention:</span>{' '}
          {attention.map((o) => o.orderName).join(', ')}
        </p>
      )}
    </div>
  );
}

/**
 * Screen-print completion toggle for a mixed batch (money-waster #1). Marking it done unblocks
 * "Mark printed"; a batch with outstanding screen-print work can't be marked printed, so a line
 * never reads "done" while its screen-print half is unproduced. Only shown when the batch has
 * screen-print work and is still in progress (DRAFT/READY).
 */
function ScreenPrintControl({
  batchId,
  status,
  hasScreenPrintWork,
  done,
  onChanged,
}: {
  batchId: string;
  status: BatchStatus;
  hasScreenPrintWork: boolean;
  done: boolean;
  onChanged: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const toggle = useMutation({
    mutationFn: async () => {
      const res = done
        ? await client.POST('/batches/{batchId}/unmark-screenprinted', {
            params: { path: { batchId } },
          })
        : await client.POST('/batches/{batchId}/mark-screenprinted', {
            params: { path: { batchId } },
          });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Action failed.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['batch', batchId] });
      qc.invalidateQueries({ queryKey: ['batches'] });
      setConfirm(false);
      onChanged();
    },
  });

  // Screen print is done off the sheet before/around marking printed; hide once fully done/failed.
  if (!hasScreenPrintWork || (status !== 'DRAFT' && status !== 'READY')) return null;

  // Already done → a direct undo (reverses the press; recoverable, so no confirm).
  if (done) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="text-emerald-700 dark:text-emerald-300"
        onClick={() => toggle.mutate()}
        disabled={toggle.isPending}
        aria-pressed
      >
        {toggle.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Brush className="size-4" />
        )}
        Screen print done ✓
      </Button>
    );
  }

  // Pending → confirm first: this is the bulk shortcut (you normally press one-by-one at the press
  // station). It presses every placement, settles each garment, and can CONSUME a blank — so it asks.
  return (
    <Dialog open={confirm} onOpenChange={(v) => !toggle.isPending && setConfirm(v)}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          toggle.reset();
          setConfirm(true);
        }}
      >
        <Brush className="size-4" />
        Mark all pressed
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark all screen print pressed?</DialogTitle>
          <DialogDescription>
            Marks every screen-print garment below as pressed in one go. Any garment whose DTF is
            also done will complete and consume its blank. You normally press these one-by-one at
            the press station — use this only when the whole batch's screen print is finished. You
            can undo it.
          </DialogDescription>
        </DialogHeader>
        {toggle.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {toggle.error instanceof Error ? toggle.error.message : 'Failed to mark pressed.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirm(false)} disabled={toggle.isPending}>
            Cancel
          </Button>
          <Button onClick={() => toggle.mutate()} disabled={toggle.isPending}>
            {toggle.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Brush className="size-4" />
            )}
            Mark all pressed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The screen-print worklist for a batch — what to produce off the sheet (garment, location, art, qty).
 *  Its header carries the bulk "Mark all pressed" control (relocated here from the batch action row —
 *  it acts on this table, and it's a money-path action so it lives with its data + confirms). */
function ScreenPrintWorklist({
  items,
  done,
  batchId,
  status,
  onChanged,
}: {
  items: readonly ScreenPrintWorkItem[];
  done: boolean;
  batchId: string;
  status: BatchStatus;
  onChanged: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Screen print (off sheet)
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-medium normal-case',
              done
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                : 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
            )}
          >
            {done ? 'done' : 'pending'}
          </span>
        </h4>
        <div className="ml-auto">
          <ScreenPrintControl
            batchId={batchId}
            status={status}
            hasScreenPrintWork={items.length > 0}
            done={done}
            onChanged={onChanged}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Pressed one-by-one at the press station; “Mark all pressed” marks the whole batch at once.
      </p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-muted/40">
            <tr>
              <Th>Order</Th>
              <Th>Garment</Th>
              <Th>Location</Th>
              <Th>Design</Th>
              <Th className="text-right">Qty</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr
                key={`${it.storeId}#${it.orderId}#${it.lineId}#${it.location}`}
                className="border-t [&:nth-child(even)]:bg-muted/20"
              >
                <td className="px-3 py-1.5 align-top font-medium">
                  <Link
                    to="/orders/$storeId/$orderId"
                    params={{ storeId: it.storeId, orderId: it.orderId }}
                    search={orderCardSearch('batches')}
                    className="underline underline-offset-2 hover:no-underline"
                    title="Open this order's job card"
                  >
                    {it.orderName}
                  </Link>
                </td>
                <td className="px-3 py-1.5 align-top text-xs">
                  {it.title}
                  {it.variantTitle && (
                    <span className="text-muted-foreground"> · {it.variantTitle}</span>
                  )}
                </td>
                <td className="px-3 py-1.5 align-top text-xs">{it.location}</td>
                <td className="px-3 py-1.5 align-top text-xs">
                  {it.designName ?? <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-1.5 text-right align-top font-medium tabular-nums">
                  {it.quantity}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BatchActions({
  status,
  busy,
  onGenerate,
  onRetry,
}: {
  status: BatchStatus;
  busy: boolean;
  onGenerate: () => void;
  onRetry: () => void;
}) {
  if (status === 'DRAFT') {
    return (
      <Button size="sm" onClick={onGenerate} disabled={busy}>
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Layers className="size-4" />}
        Generate gang sheet
      </Button>
    );
  }
  if (status === 'FAILED') {
    return (
      <Button size="sm" onClick={onRetry} disabled={busy}>
        {busy ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
        Retry
      </Button>
    );
  }
  return null;
}

/**
 * Download every sheet in one click. All anchor clicks fire SYNCHRONOUSLY inside the button's click
 * handler so they share a single user gesture — the browser then shows ONE "allow multiple downloads"
 * prompt and, once allowed, saves them all. Each presigned URL already carries a
 * `content-disposition: attachment` filename, so the files land named (Sheet-<n>-of-<total>.png).
 *
 * Do NOT reintroduce a setTimeout/await between clicks: the delay pushes the later clicks outside the
 * user gesture, and the browser silently DROPS them — the previous 400ms-stagger version downloaded
 * only the first 2-3 of N. Browser behaviour, so it can't be unit-tested here; verify in a real
 * browser after any change.
 */
function downloadSheets(urls: readonly string[]): void {
  for (const url of urls) {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

/** One composited sheet, as the detail route serves it (dims + placed copies + per-sheet preview). */
type SheetSummary = GangSheetResult['sheets'][number];

/**
 * The film + the sheets, side by side on a wide card and stacked below ~1000px (a CONTAINER query, so
 * the same rule holds whether the card sits beside the rail or fills a phone). Owns which sheet is
 * being previewed; the Sheets card is the selector, the Film panel the view.
 */
function FilmAndSheets({ gangSheet, batchId }: { gangSheet: GangSheetResult; batchId: string }) {
  const [selected, setSelected] = useState(0);
  const idx = Math.min(selected, Math.max(0, gangSheet.sheets.length - 1));
  const current = gangSheet.sheets[idx];
  // Per-sheet preview when available; sheet 0 (and pre-per-sheet-preview batches) fall back to the
  // top-level previewUrl. A non-first sheet on a legacy batch has no preview → the panel says so.
  const previewUrl = current?.previewUrl ?? (idx === 0 ? gangSheet.previewUrl : undefined);
  return (
    <div className="@container">
      <div className="grid items-start gap-4 @min-[1000px]:grid-cols-[minmax(0,1fr)_320px]">
        {current && (
          <FilmPanel
            sheet={current}
            index={idx}
            total={gangSheet.sheetCount}
            previewUrl={previewUrl}
          />
        )}
        <SheetsCard gangSheet={gangSheet} batchId={batchId} selected={idx} onSelect={setSelected} />
      </div>
    </div>
  );
}

/**
 * ONE sheet's preview, fit by pixels-per-inch (`previewFit` — "rotate only when it buys pixels"), with
 * an inch ruler along the axis that runs across the panel and a full-size view.
 *
 * Replaces a panel that turned EVERY sheet 90° and sized its box to the rotated aspect with no height
 * cap. The nester trims a sheet to its content, so a one-line reprint is a short WIDE strip — 52 of the
 * 194 live sheets are — and standing it on end drew a 12.8″ sheet as a 900 × 2,460px box and a 2.87″
 * strip as 900 × 10,988px of mostly blank film (measured live, 2026-09-27). Now the box never exceeds
 * `previewBoxHeight` and a long sheet still lands landscape exactly as before.
 *
 * The panel measures its own width (ResizeObserver; the first paint and jsdom read 0 and fall back to
 * a sane width) so the fit is computed against the real box, not a guess.
 */
function FilmPanel({
  sheet,
  index,
  total,
  previewUrl,
}: {
  sheet: SheetSummary;
  index: number;
  total: number;
  previewUrl: string | undefined;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxWidth, setBoxWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 900 : window.innerHeight,
  );
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      setBoxWidth(el.clientWidth);
      setViewportHeight(window.innerHeight);
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  const [zoom, setZoom] = useState(false);

  // The measured box less the panel's own padding; 720 until the first measurement lands.
  const box = {
    width: boxWidth > 0 ? boxWidth - 24 : 720,
    height: previewBoxHeight(viewportHeight),
  };
  const fit = previewFit(sheet, box);
  const acrossIn = fit.rotated ? fit.lengthIn : fit.widthIn;
  // Full size = the crisp ceiling (2× the PNG's native resolution), always as printed; the dialog
  // scrolls. Once the compositor emits wider previews this is where they show.
  const zoomFit = previewFit(sheet, {
    width: PREVIEW_NATIVE_WIDTH_PX * PREVIEW_MAX_UPSCALE,
    height: Number.POSITIVE_INFINITY,
  });
  const dims = `${formatInches(fit.widthIn)}″ × ${formatInches(fit.lengthIn)}″`;
  const label = `Sheet ${index + 1}${total > 1 ? ` of ${total}` : ''}`;

  return (
    <div className="min-w-0 rounded-lg border" data-slot="film-panel">
      <div className="flex items-center gap-2 border-b px-3 py-2 text-sm font-semibold">
        Film
        {total > 1 && (
          <span className="text-xs font-medium text-muted-foreground">
            sheet {index + 1} of {total}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() => setZoom(true)}
          disabled={!previewUrl}
        >
          <Maximize2 className="size-4" />
          Full size
        </Button>
      </div>
      <div className="p-3">
        <div ref={boxRef} className="flex justify-center rounded-md border bg-muted/40 p-3">
          {previewUrl ? (
            <div>
              <FilmImage url={previewUrl} fit={fit} alt={`Gang-sheet preview — ${label}`} />
              <Ruler inches={acrossIn} px={fit.width} />
            </div>
          ) : (
            <p className="py-6 text-center text-xs text-muted-foreground">
              No preview for this sheet — it was built before per-sheet previews. Regenerate the
              batch to add one.
            </p>
          )}
        </div>
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <b className="font-semibold text-foreground">{label}</b>
          <span className="tabular-nums">{dims}</span>
          <span>
            {sheet.placementCount} print{sheet.placementCount === 1 ? '' : 's'}
          </span>
          <span>{fit.rotated ? 'turned 90°' : 'as printed'}</span>
          <span className="tabular-nums">1 in ≈ {Math.round(fit.pxPerIn)} px</span>
        </p>
      </div>
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-h-[90vh] sm:max-w-[min(96vw,1720px)]">
          <DialogHeader>
            <DialogTitle>{label} — full size</DialogTitle>
            <DialogDescription>
              {dims} · {sheet.placementCount} print{sheet.placementCount === 1 ? '' : 's'} · 1 in ≈{' '}
              {Math.round(zoomFit.pxPerIn)} px · scroll to pan
            </DialogDescription>
          </DialogHeader>
          {/* The scroll lives on THIS box, never on DialogContent — its close button must stay put. */}
          <div className="max-h-[75vh] overflow-auto rounded-md border bg-muted/40 p-3">
            {previewUrl && (
              <FilmImage
                url={previewUrl}
                fit={zoomFit}
                alt={`Gang-sheet preview — ${label}, full size`}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The preview image inside a box of exactly the fit's footprint. When the fit is rotated the image
 * element is sized to the UNROTATED sheet (width across, length down), centred, and turned 90° — the
 * same pixel aspect, no distortion, and the box around it is what the layout sees.
 */
function FilmImage({ url, fit, alt }: { url: string; fit: PreviewFit; alt: string }) {
  return (
    <div
      className="relative overflow-hidden"
      style={{ width: fit.width, height: fit.height }}
      data-testid="film"
      data-rotated={fit.rotated}
    >
      <img
        key={url}
        src={url}
        alt={alt}
        className={cn(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded bg-[repeating-conic-gradient(#e5e5e5_0_25%,transparent_0_50%)] bg-[length:14px_14px] shadow-sm ring-1 ring-border',
          fit.rotated && 'rotate-90',
        )}
        style={
          fit.rotated
            ? { width: fit.height, height: fit.width }
            : { width: fit.width, height: fit.height }
        }
      />
    </div>
  );
}

/** Inch ticks under the film, along the axis that runs across the panel. Decorative — the caption
 *  carries the dimensions in text. */
function Ruler({ inches, px }: { inches: number; px: number }) {
  return (
    <div
      className="relative mt-1.5 h-4 text-[9.5px] text-muted-foreground tabular-nums"
      style={{ width: px }}
      aria-hidden="true"
    >
      {rulerTicks(inches, px).map((t) => (
        <span
          key={t.left}
          className={cn('absolute top-0 w-px bg-muted-foreground/60', t.label ? 'h-2' : 'h-1.5')}
          style={{ left: t.left }}
        >
          {t.label && (
            <span className="absolute top-2 left-0 -translate-x-1/2 whitespace-nowrap">
              {t.label}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * The physical sheets of the batch — one row each with a thumbnail, its size and print count, the
 * PRINTED tick and its download; click a row to preview it. Replaces a run of text rows above a
 * detached preview.
 *
 * Per-sheet PRINT check-off: mark each sheet as it's run through the printer, so a job spanning days is
 * resumable (you can see sheet 1 is printed but 2–3 aren't). Pure tracking — it does NOT consume blanks
 * or mark the batch done (that's the separate "Mark printed" control). The mutation is unchanged from
 * the previous panel.
 */
function SheetsCard({
  gangSheet,
  batchId,
  selected,
  onSelect,
}: {
  gangSheet: GangSheetResult;
  batchId: string;
  selected: number;
  onSelect: (index: number) => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const printedCount = gangSheet.sheets.filter((s) => s.printedAt).length;
  const allPrinted = gangSheet.sheetCount > 0 && printedCount === gangSheet.sheetCount;

  const markSheet = useMutation({
    mutationFn: async (v: { sheetIndex: number; printed: boolean }) => {
      const res = await client.POST('/batches/{batchId}/sheets/{sheetIndex}/print', {
        params: { path: { batchId, sheetIndex: String(v.sheetIndex) } },
        body: { printed: v.printed },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to mark the sheet.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    // Refetch so sheets[].printedAt reflects the toggle (server is the single source of truth).
    onSettled: () => qc.invalidateQueries({ queryKey: ['batch', batchId] }),
  });

  return (
    <div className="min-w-0 rounded-lg border">
      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2 text-sm font-semibold">
        Sheets
        <span
          className={cn(
            'text-xs font-medium tabular-nums text-muted-foreground',
            allPrinted && 'text-emerald-600 dark:text-emerald-400',
          )}
        >
          {printedCount}/{gangSheet.sheetCount} printed
        </span>
        <span className="ml-auto text-xs font-normal text-muted-foreground">
          {Math.round(gangSheet.densityPct * 100)}% film density
        </span>
      </div>
      <div className="space-y-1.5 p-2">
        <span className="block h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <span
            className="block h-full rounded-full bg-emerald-500 transition-[width]"
            style={{
              width: `${gangSheet.sheetCount ? (printedCount / gangSheet.sheetCount) * 100 : 0}%`,
            }}
          />
        </span>
        {gangSheet.printReadyUrls.map((url, i) => {
          const s = gangSheet.sheets[i];
          const printed = Boolean(s?.printedAt);
          const thumbUrl = s?.previewUrl ?? (i === 0 ? gangSheet.previewUrl : undefined);
          return (
            <div
              // The print-ready URL is presigned and changes on every refetch; the sheet index is stable.
              key={s ? `sheet-${s.sheetIndex}` : url}
              className={cn(
                'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-1.5 rounded-md border px-2 py-1.5',
                i === selected &&
                  'border-foreground bg-muted/30 shadow-[inset_3px_0_0_0_var(--foreground)]',
              )}
            >
              {/* Click the sheet to preview it (distinct from the Printed toggle + Download). */}
              <button
                type="button"
                onClick={() => onSelect(i)}
                aria-pressed={i === selected}
                aria-label={`Preview sheet ${i + 1}`}
                className="flex min-w-0 items-center gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <SheetThumb sheet={s} url={thumbUrl} />
                <span className="min-w-0">
                  <span
                    className={cn(
                      'block text-sm font-medium',
                      printed && 'text-muted-foreground line-through',
                    )}
                  >
                    Sheet {i + 1}
                  </span>
                  {s && (
                    <span className="block text-xs text-muted-foreground tabular-nums">
                      {sheetSizeLabel(s)} · {s.placementCount} print
                      {s.placementCount === 1 ? '' : 's'}
                    </span>
                  )}
                </span>
              </button>
              <label
                htmlFor={`sheet-printed-${i}`}
                className="flex cursor-pointer select-none items-center gap-1.5 text-xs text-muted-foreground"
              >
                <Checkbox
                  id={`sheet-printed-${i}`}
                  aria-label={`Sheet ${i + 1} printed`}
                  checked={printed}
                  disabled={markSheet.isPending}
                  onCheckedChange={(v) => markSheet.mutate({ sheetIndex: i, printed: Boolean(v) })}
                />
                Printed
              </label>
              <Button size="sm" variant="outline" className="px-2" asChild>
                {/* No target=_blank: the URL's content-disposition makes the click download the file
                    (with a meaningful name) rather than open the PNG in a new tab. */}
                <a href={url} download rel="noreferrer" aria-label={`Download sheet ${i + 1}`}>
                  <Download className="size-4" />
                </a>
              </Button>
            </div>
          );
        })}
        {markSheet.isError && (
          // A failed toggle (e.g. a 409 when another operator marked a sheet first) silently reverts
          // the checkbox on refetch — surface why, so staff know to retry rather than re-clicking blind.
          <p className="text-xs text-destructive">
            {markSheet.error instanceof Error
              ? markSheet.error.message
              : 'Couldn’t update the sheet — refresh and retry.'}
          </p>
        )}
      </div>
    </div>
  );
}

/** A 64×40 thumbnail of one sheet, fit the same way the big preview is (turned when that shows more). */
function SheetThumb({ sheet, url }: { sheet: SheetSummary | undefined; url: string | undefined }) {
  if (!sheet || !url) {
    return <span className="block h-10 w-16 shrink-0 rounded bg-muted" aria-hidden="true" />;
  }
  const fit = previewFit(sheet, { width: 64, height: 40 });
  return (
    <span className="flex h-10 w-16 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
      <FilmImage url={url} fit={fit} alt="" />
    </span>
  );
}

/** Past this many rows the items table starts collapsed — a 49-line batch has 98 rows (3,658px). */
const ITEMS_COLLAPSE_PAST = 12;

function ItemsSection({ items }: { items: BatchDetail['items'] }) {
  const [open, setOpen] = useState(items.length <= ITEMS_COLLAPSE_PAST);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold"
        >
          Items
          <span className="text-xs font-medium text-muted-foreground">
            {items.length} placement{items.length === 1 ? '' : 's'}
          </span>
          <ChevronDown
            className={cn('ml-auto size-4 transition-transform', !open && '-rotate-90')}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t">
        <ItemsTable items={items} />
      </CollapsibleContent>
    </Collapsible>
  );
}

function ItemsTable({ items }: { items: BatchDetail['items'] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm">
        <thead className="bg-muted/40">
          <tr>
            <Th>Design</Th>
            <Th>SKU</Th>
            <Th className="text-right">Size (in)</Th>
            <Th className="text-right">Qty</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={`${item.storeId}#${item.orderId}#${item.lineId}`}
              className="border-t [&:nth-child(even)]:bg-muted/20"
            >
              <td className="px-3 py-2 align-top text-xs">
                {item.designName ?? (
                  <span className="font-mono text-muted-foreground">
                    {item.designId.slice(0, 8)}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 align-top font-mono text-xs text-muted-foreground">
                {item.sku ?? '—'}
              </td>
              <td className="px-3 py-2 text-right align-top tabular-nums text-muted-foreground">
                {formatInches(item.widthIn)} × {formatInches(item.heightIn)}
              </td>
              <td className="px-3 py-2 text-right align-top font-medium tabular-nums">
                {item.quantity}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One step of the action bar — a tiny uppercase label and its buttons. */
function ActionGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

/** A compact count chip for the batch header (lines / pieces / designs). */
function StatChip({ value, label }: { value: number; label: string }) {
  return (
    <span className="inline-flex items-baseline gap-1 rounded-md border bg-muted/40 px-2 py-0.5 text-xs">
      <b className="text-sm font-semibold tabular-nums">{value}</b>
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn('px-3 py-2 text-left text-xs font-medium text-muted-foreground', className)}>
      {children}
    </th>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
      <Layers className="size-8 text-muted-foreground" />
      <p className="text-sm font-medium">Nothing to print</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        Select mapped orders on the Orders page and Build — the sheet opens here while it
        composites.
      </p>
      <p className="max-w-sm text-xs text-muted-foreground">
        Looking for an old sheet? It's under “Printed” in the list.
      </p>
    </div>
  );
}
